import React, { useState } from 'react';
import { Box, Container, Grid, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import feQuestion from '../../assets/images/fe_question.png';
import '../../App.css';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useStagger from '../../hooks/useStagger';

const faqs = [
  {
    id: 'faq1',
    question: 'What does your platform do?',
    answer: 'OTC Brokerage: Buy and sell cryptocurrency directly with fast, non-custodial execution.',
  },
  {
    id: 'faq2',
    question: 'Do you hold my cryptocurrency?',
    answer: 'No, we do not. We are non-custodial, which means we do not hold or store your cryptocurrency. All crypto purchases are sent directly to your wallet address after the payment is received. Your coins, your keys.',
  },
  {
    id: 'faq3',
    question: 'Do I need to complete identity verification (KYC)?',
    answer: 'Yes, to comply with regulatory requirements, customers must complete identity verification (KYC). This helps us maintain a safe and compliant trading environment.',
  },
  {
    id: 'faq4',
    question: 'How long does a transaction take?',
    answer: 'Transaction times depend on the payment method and blockchain network. Most payments (subjected to your bank) are completed within minutes and in some instances, it could take 3 business days. As soon as payment is received, cryptocurrency will be settled immediately as our team operates 24/7.',
  },
  {
    id: 'faq5',
    question: 'What fees do you charge?',
    answer: 'We do not charge a transaction fee. We make our margin from the price spread.',
  },
  {
    id: 'faq6',
    question: 'What happens if I provide the wrong wallet address?',
    answer: 'Cryptocurrency transactions are irreversible. Please double-check your wallet address before confirming your order as we cannot recover funds sent to an incorrect address.',
  },
];

const FaqSection = () => {
  const [expanded, setExpanded] = useState('');
  const [leftRef, leftVisible] = useScrollAnimation(0.15);
  const faqListRef = useStagger({ delay: 100, translateY: 28, duration: 1050, threshold: 0.06 });

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 10, md: 8 },
        backgroundColor: 'var(--color-bg)',
        color: '#fff',
      }}
    >
      <Container>
        <Grid container spacing={{ xs: 5, md: 10 }} sx={{ justifyContent: 'center' }}>
          {/* Left Title Column */}
          <Grid
            size={{ xs: 12, md: 6 }}
            ref={leftRef}
            className={`anim-fade-left${leftVisible ? ' in-view' : ''}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: { xs: 'center', md: 'flex-start' },
                textAlign: { xs: 'center', md: 'left' },
                height: '100%',
                width: '100%',
              }}
            >
              <Typography
                variant="h2"
                className="faq-title"
                sx={{
                  fontSize: {
                    xs: '2rem',
                    sm: '2.5rem',
                    md: '2.9rem',
                  },
                  mb: 3,
                  letterSpacing: '-0.02em',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Frequently Asked
                <br />
                Questions
              </Typography>

              <Typography
                className="faq-desc"
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Have questions? We've got answers. Browse some of our most common questions, and if you can't find what you're looking for, our team is here to help.
              </Typography>
            </Box>
          </Grid>

          {/* Right Accordion Column */}
          <Grid
            size={{ xs: 12, md: 6 }}
          >
            <Box
              ref={faqListRef}
              sx={{
                width: "100%",
              }}
            >
              {faqs.map((faq, index) => {
                const isCurrentExpanded = expanded === faq.id;

                return (
                  <Accordion
                    key={faq.id}
                    id={`faq-accordion-${faq.id}`}
                    expanded={isCurrentExpanded}
                    onChange={handleChange(faq.id)}
                    elevation={0}
                    disableGutters
                    sx={{
                      backgroundColor: isCurrentExpanded ? '#2FCCB221' : 'var(--color-bg)',
                      borderBottom: index !== faqs.length - 1 ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
                      boxShadow: 'none',
                      borderRadius: '0px',
                      transition: 'all 0.3s ease',
                      '&:before': {
                        display: 'none',
                      },
                      '&:hover': {
                        backgroundColor: isCurrentExpanded ? '#2FCCB221' : '#2FCCB210',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{
                            color: isCurrentExpanded ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.5)',
                            transition: 'transform 0.3s ease',
                            transform: isCurrentExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            fontSize: '1.1rem',
                          }}
                        />
                      }
                      id={`faq-summary-${faq.id}`}
                      sx={{
                        px: 1.5,
                        py: 1.5,
                        minHeight: 'unset',
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                        },
                      }}
                    >
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.85rem', md: '0.9rem' },
                          fontWeight: 600,
                          color: isCurrentExpanded ? 'var(--color-primary)' : '#FFFFFF',
                          letterSpacing: '-0.01em',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        <img
                          src={feQuestion}
                          alt="icon"
                          style={{ width: 22, height: 22 }}
                        />
                        <span>{faq.question}</span>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        px: 1.5,
                        pb: 2,
                        pt: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: '0.8rem', md: '0.85rem' },
                          lineHeight: 1.6,
                          color: 'rgba(255, 255, 255, 0.7)',
                        }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FaqSection;
